import { TFunction } from 'i18next'

/**
 * The reader never sees text a server or provider wrote for a failed turn.
 *
 * A terminal `error` frame carries a `code` from the frozen
 * `AssistantErrorCode` set; the sentence the reader gets is our own localized
 * copy for that code. The frame's `message` is deliberately dropped here: it
 * can only ever be exception text on its way from a provider (a billing or
 * quota notice, a URL), and full detail belongs in the server logs.
 */
export interface AssistantStreamFailure extends Error {
  assistantErrorCode: string
}

/** Error thrown for an `error` frame. Its `message` is a constant, never the frame's. */
export function createAssistantStreamFailure(frame: {
  code?: string
}): AssistantStreamFailure {
  const failure = new Error('Assistant turn failed') as AssistantStreamFailure
  failure.assistantErrorCode =
    typeof frame.code === 'string' && frame.code ? frame.code : 'internal'
  return failure
}

export function isAssistantStreamFailure(
  error: unknown,
): error is AssistantStreamFailure {
  return (
    error instanceof Error &&
    typeof (error as Partial<AssistantStreamFailure>).assistantErrorCode ===
      'string'
  )
}

function copyForCode(code: string, t: TFunction<'app'>): string {
  switch (code) {
    case 'model_failed':
      return t('threads.detail.turnError.model_failed')
    case 'timeout':
      return t('threads.detail.turnError.timeout')
    case 'rate_limited':
      return t('threads.detail.turnError.rate_limited')
    case 'retrieval_failed':
      return t('threads.detail.turnError.retrieval_failed')
    default:
      return t('threads.detail.turnError.generic')
  }
}

/**
 * A client-error refusal Nest wrote itself (its JSON envelope with a 4xx
 * `statusCode`), for example the empty-thread report refusal. Anything else,
 * a 5xx body included, is not for the reader.
 */
export function nestRefusalMessage(error: Error): string | null {
  const text = error.message.trim()
  if (!text.startsWith('{')) {
    return null
  }
  try {
    const parsed = JSON.parse(text) as {
      statusCode?: unknown
      message?: unknown
    }
    if (
      typeof parsed.statusCode === 'number' &&
      parsed.statusCode >= 400 &&
      parsed.statusCode < 500 &&
      typeof parsed.message === 'string' &&
      parsed.message.trim()
    ) {
      return parsed.message.trim()
    }
  } catch {
    return null
  }
  return null
}

/**
 * The one sentence a failed assistant turn shows (toast and thread bubble).
 * A stream `error` frame maps by code; a refusal Nest wrote is shown as is;
 * every other failure (a dropped connection, a 5xx body, an unknown throw)
 * is the generic "did not finish" copy.
 */
export function assistantTurnFailureText(
  error: unknown,
  t: TFunction<'app'>,
): string {
  if ((error as { code?: unknown } | null)?.code === 'insufficient_credits') {
    return t('threads.detail.turnError.insufficient_credits')
  }
  if (isAssistantStreamFailure(error)) {
    return copyForCode(error.assistantErrorCode, t)
  }
  if (error instanceof Error) {
    const refusal = nestRefusalMessage(error)
    if (refusal) {
      return refusal
    }
  }
  return t('threads.detail.turnError.generic')
}
